import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Chip, Divider, Grid, Typography, useTheme } from "@mui/material";
import React from "react";
import { ChatIcon } from "src/assets/icons";
import FlagIcon from "src/containers/FlagIcon/FlagIcon";
import { TravelExpenseAuditNotesDTO } from "src/models/travel-expense-audit-notes";
import { intlFormatDateShort, intlFormatDateToHour } from "src/utils/time-utils";
import {
  FieldValue,
  DetailsContainer,
  Field,
  InfoContainer,
  Label,
  Status,
  StatusCountryContainer,
  TravelExpenseWrapper,
} from "./styles";
import { EmployeeRef } from "../../../../../../../../models/employees";
import { TravelExpenseRef } from "../../../../../../../../models/travel-expenses";
import { ExpenseReportView } from "../../../../../utils";
import TypeAndDescription from "../ReceiptComponents/TypeAndDescription/TypeAndDescription";
export interface Props {
  travelExpense: TravelExpenseRef;
  allTravelExpenses: TravelExpenseRef[];
  view: ExpenseReportView;
  expenseReportStatusId?: string;
  readOnly: boolean;
  employee: EmployeeRef;
  hideAuditNotes: boolean;
  preloadedFlags?: Record<string, string>;
  isAuditor: boolean;
  allAuditNotes: TravelExpenseAuditNotesDTO[];
}

const ExportedTravelExpenseReceipt = ({
  travelExpense,
  allTravelExpenses,
  view,
  expenseReportStatusId,
  readOnly,
  employee,
  hideAuditNotes,
  preloadedFlags,
  isAuditor,
  allAuditNotes,
}: Props) => {
  const theme = useTheme();
  return (
    <TravelExpenseWrapper>
      <InfoContainer flex={3}>
        <Grid style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <TypeAndDescription
            type={travelExpense.type?.name ?? ""}
            flex={1}
            typeIconContent={travelExpense?.type?.iconContent}
            date={travelExpense.date}
            description={travelExpense.description}
          />
          {!travelExpense.includeNotesInExportedReport && allAuditNotes.length > 0 && (
            <ChatIcon color={theme.palette.primary.main} />
          )}
        </Grid>

        <DetailsContainer>
          <Field>
            <Label>Amount :</Label>
            <FieldValue>{`${travelExpense.amount.toLocaleCustom()} ${travelExpense.currency?.code}`}</FieldValue>
          </Field>
          <Field>
            <Label>Accepted amount :</Label>
            <FieldValue color={theme.palette.success.main}>{`${travelExpense.acceptedAmount.toLocaleCustom()} ${
              travelExpense.currency?.code
            }`}</FieldValue>
          </Field>
          <Field>
            <Label>VAT amount :</Label>
            <FieldValue>{`${travelExpense.vatAmount.toLocaleCustom()} ${travelExpense.currency?.code}`}</FieldValue>
          </Field>
          <Field>
            <Label>VAT rate :</Label>
            <FieldValue>{`${travelExpense.vatRate.toLocaleCustom()}%`}</FieldValue>
          </Field>
          {isAuditor && (
            <>
              <Field style={{ display: "block" }}>
                <Label>
                  Was the cost really incurred ? :{" "}
                  {travelExpense.costIncurred && (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      color={theme.palette.success.main}
                    />
                  )}
                </Label>
                <FieldValue>{travelExpense.costIncurredComment ?? ""}</FieldValue>
              </Field>
              <Field style={{ display: "block" }}>
                <Label>
                  The amount and expenses type are justified for business purposes ? :{" "}
                  {travelExpense.amountAndTypeJustified && (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      color={theme.palette.success.main}
                    />
                  )}
                </Label>
                <FieldValue>{travelExpense.amountAndTypeJustifiedComment ?? ""}</FieldValue>
              </Field>
              <Field style={{ display: "block" }}>
                <Label>
                  Is the receipt readable and compliant ? :{" "}
                  {travelExpense.compliantReceipt && (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      color={theme.palette.success.main}
                    />
                  )}
                </Label>
                <FieldValue>{travelExpense.compliantReceiptComment ?? ""}</FieldValue>
              </Field>
              {travelExpense.includeNotesInExportedReport && (
                <>
                  <Divider
                    orientation="horizontal"
                    style={{
                      alignItems: "start",
                      marginBottom: "20px",
                    }}
                  >
                    <Chip label="Audit notes " />
                  </Divider>
                  <Field style={{ display: "block" }}>
                    <Grid style={{ borderLeft: `1px solid ${theme.palette.primary.main}`, padding: "10px" }}>
                      {allAuditNotes.length > 0 ? (
                        allAuditNotes.map((item) => {
                          const sendMessageTime = `${intlFormatDateShort(
                            new Date(item.createdAt)
                          )} ${intlFormatDateToHour(new Date(item.createdAt))}`;

                          return (
                            <React.Fragment key={item.id}>
                              <Grid>
                                <Grid
                                  sx={{
                                    "& p": {
                                      display: "inline",
                                      color: theme.palette.text.disabled,
                                    },
                                  }}
                                >
                                  <Typography>{item.createdByUser!.name}</Typography>
                                  &nbsp;
                                  <Typography variant="body2">
                                    {item.asAuditor ? "(auditor)" : item.asManager ? "(manager)" : ""}
                                  </Typography>
                                  &nbsp;
                                  <Typography variant="body2">{sendMessageTime}</Typography>
                                </Grid>

                                <Grid>
                                  {item.auditDocument && (
                                    <Typography
                                      variant="body2"
                                      style={{ color: theme.palette.secondary.light }}
                                    >
                                      <i>&lt; Uploaded document &gt;</i>
                                    </Typography>
                                  )}
                                  <Typography>{item.notes}</Typography>
                                </Grid>
                              </Grid>
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <Typography
                          variant="body2"
                          style={{ color: theme.palette.secondary.light }}
                        >
                          {" "}
                          <i> &lt; No audit notes &gt; </i>{" "}
                        </Typography>
                      )}
                    </Grid>
                  </Field>
                </>
              )}
            </>
          )}
        </DetailsContainer>
      </InfoContainer>
      <StatusCountryContainer
        auditAccepted={travelExpense.auditAccepted}
        flex={0.5}
      >
        <Status auditAccepted={travelExpense.auditAccepted}>
          {travelExpense.auditAccepted === null ? "Pending" : travelExpense.auditAccepted ? "Accepted" : "Rejected"}
        </Status>
        {!!travelExpense.country?.code && (
          <Grid style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <FlagIcon
              countryCode={travelExpense.country?.code}
              customStyles={{ borderRadius: "8px", width: "50px", height: "38px" }}
              predefinedSRC={preloadedFlags?.[travelExpense.country.code]}
            />
            <Typography variant="body2">
              <i>{`(${travelExpense.country.name})`}</i>
            </Typography>
          </Grid>
        )}
      </StatusCountryContainer>
    </TravelExpenseWrapper>
  );
};
export default ExportedTravelExpenseReceipt;
